import { useCallback, useEffect, useState } from "react";

import * as api from "@/api/client";
import type { PluginDriftEvent } from "@/types/api";

/**
 * Polls GET /v1/admin/plugin-drift/recent every 60s. Same shape as
 * useVigilBrandsPoll — isolated state, plain useEffect + setInterval.
 * Fixed 7-day window: enough to spot a drift trend without the panel
 * needing its own days-picker yet (add one if PK asks for a wider lens).
 */
const PLUGIN_DRIFT_INTERVAL_MS = 60_000;
const PLUGIN_DRIFT_WINDOW_DAYS = 7;

export interface PluginDriftPollState {
  events: PluginDriftEvent[];
  loading: boolean;
  error: string | null;
  days: number;
}

export function usePluginDriftPoll(): PluginDriftPollState {
  const [events, setEvents] = useState<PluginDriftEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getPluginDriftRecent({ days: PLUGIN_DRIFT_WINDOW_DAYS });
      setEvents(res.events);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Błąd pobierania driftu selektorów");
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function init() {
      setLoading(true);
      await load();
      if (!cancelled) setLoading(false);
    }

    void init();

    const timer = setInterval(() => {
      void load();
    }, PLUGIN_DRIFT_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [load]);

  return { events, loading, error, days: PLUGIN_DRIFT_WINDOW_DAYS };
}
